import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, Deal, fmtDay, money, Quote, QUOTE_STATUS_CLASS } from '../api';
import { useSession } from '../session';
import { ErrorBox, SourceBadge, useAsync, fmtDate } from '../components/ui';
import { Timeline } from '../components/Timeline';
import { AgentPanel } from '../components/AgentPanel';
import { TaskList } from '../components/TaskList';
import { requiredHint, useMoveDeal } from '../components/MoveDeal';
import { DealForm, DealInput } from './DealForm';

const STATUS: Record<string, string> = { open: 'nyitott', won: 'megnyert', lost: 'elvesztett' };
const STATUS_Q: Record<string, string> = { draft: 'piszkozat', pending_approval: 'jóváhagyásra vár', approved: 'küldhető', sent: 'elküldve', accepted: 'elfogadva', rejected: 'elutasítva', expired: 'lejárt', superseded: 'lecserélve' };

export function DealPage() {
  const { id } = useParams();
  const s = useSession();
  const nav = useNavigate();
  const [d, setD] = useState<Deal | null>(null);
  const [lostReasons, setLostReasons] = useState<string[]>([]);
  const [editing, setEditing] = useState(false);
  const [tick, setTick] = useState(0);
  const load = useAsync();
  const save = useAsync();
  const reload = () => load.run(async () => setD(await api<Deal>(`/deals/${id}`)));
  useEffect(() => { reload(); setEditing(false); }, [id]);
  useEffect(() => { api<{ lost_reasons: string[] }>('/pipelines').then((r) => setLostReasons(r.lost_reasons)).catch(() => undefined); }, []);
  const refresh = () => { reload(); setTick((x) => x + 1); };
  const { move, dialog, error: moveError, busy: moving } = useMoveDeal(lostReasons, refresh);

  const update = async (v: DealInput) => {
    if (await save.run(() => api(`/deals/${id}`, { method: 'PATCH', body: v }))) { setEditing(false); refresh(); }
  };
  const [invoices, setInvoices] = useState<any[]>([]);
  useEffect(() => { if (s.role !== 'support') api<{ items: any[] }>(`/invoices?deal_id=${id}&limit=50`).then((r) => setInvoices(r.items)).catch(() => setInvoices([])); }, [id, tick]);
  const newQuote = async () => {
    const r = await save.run(() => api<Quote>('/quotes', { method: 'POST', body: { deal_id: id } }));
    if (r) nav(`/ajanlatok/${r.id}`);
  };
  const archive = async () => {
    if (!window.confirm('Archiválod a dealt? 30 napig visszaállítható.')) return;
    if (await save.run(() => api(`/deals/${id}/archive`, { method: 'POST' }))) nav('/ertekesites');
  };

  if (load.error) return <ErrorBox error={load.error} />;
  if (!d) return <div className="muted">Betöltés…</div>;
  const fs = d.field_sources;
  const stages = d.pipeline?.stages ?? [];
  return (
    <div className="detail">
      <div className="page-head">
        <h2>{d.title} <span className={`badge ${d.status === 'won' ? 'badge-agent' : ''}`}>{STATUS[d.status]}</span></h2>
        <div className="spacer" />
        <Link to="/ertekesites">← Tábla</Link>
        {!editing && <button onClick={() => setEditing(true)}>Szerkesztés</button>}
        {s.role === 'owner' && <button className="danger" onClick={archive}>Archiválás</button>}
      </div>
      {d.deleted_at && <div className="notice">Archivált deal ({fmtDate(d.deleted_at)}).</div>}
      <ol className="stepper" aria-label="Lépések">
        {stages.map((st) => {
          const idx = stages.findIndex((x) => x.id === d.stage_id);
          const cls = st.id === d.stage_id ? 'current' : st.kind === 'open' && st.position < (stages[idx]?.position ?? 0) ? 'past' : '';
          return (
            <li key={st.id} className={`${cls} kind-${st.kind}`}>
              <button className="link" disabled={moving || st.id === d.stage_id || !!d.deleted_at} title={requiredHint(st)} onClick={() => move(d, st)}>{st.name}</button>
            </li>
          );
        })}
      </ol>
      {moveError}
      {dialog}
      {d.is_stale && <div className="notice">Elakadt: {d.days_in_stage} napja nem mozdult ebben a lépésben. Írj ki egy következő lépést, vagy léptesd tovább.</div>}
      {d.status === 'open' && !d.next_task && !d.is_stale && <div className="notice">Nincs nyitott feladat ezen a dealen – mi a következő lépés?</div>}
      <div className="cols">
        <div>
          <section className="card">
            {editing ? (
              <DealForm initial={d} onSubmit={update} onCancel={() => setEditing(false)} error={save.error} busy={save.busy} />
            ) : (
              <dl className="props">
                <dt>Cég</dt><dd>{d.company_id ? <Link to={`/cegek/${d.company_id}`}>{d.company_name}</Link> : '–'}</dd>
                <dt>Érték</dt><dd>{money(d.value, d.currency)} <SourceBadge src={fs.value} /></dd>
                <dt>Valószínűség</dt><dd>{d.effective_probability}%{d.probability == null && <span className="muted"> (a lépés szerint)</span>}</dd>
                <dt>Súlyozott érték</dt><dd>{money(d.weighted_value, d.currency)}</dd>
                <dt>Várható zárás</dt><dd>{fmtDay(d.expected_close)} <SourceBadge src={fs.expected_close} /></dd>
                <dt>Felelős</dt><dd>{d.owner_name ?? '–'}</dd>
                <dt>Következő lépés</dt><dd>{d.next_step ?? <span className="muted">–</span>} <SourceBadge src={fs.next_step} /></dd>
                {d.status === 'lost' && <><dt>Elvesztés oka</dt><dd>{d.lost_reason}</dd></>}
                <dt>Lépésben</dt><dd>{d.days_in_stage ?? 0} napja</dd>
                <dt>Címkék</dt><dd>{d.tags.length ? d.tags.map((t) => <span key={t} className="tag">{t}</span>) : '–'}</dd>
              </dl>
            )}
          </section>
          <section className="card">
            <h3>Kontaktok</h3>
            <ul className="plain">
              {d.contacts?.map((k) => (
                <li key={k.id}><Link to={`/kontaktok/${k.id}`}>{k.full_name}</Link>
                  {k.position && <span className="muted"> · {k.position}</span>}{k.email && <span className="muted"> · {k.email}</span>}</li>
              ))}
              {d.contacts?.length === 0 && <li className="muted">Nincs kontakt a dealben. A Szerkesztésnél a cég kontaktjai közül választhatsz.</li>}
            </ul>
          </section>
          <section className="card">
            <div className="card-head"><h3>Ajánlatok</h3><button className="secondary" onClick={newQuote} disabled={save.busy}>Új ajánlat</button></div>
            <ul className="plain">
              {(d as any).quotes?.map((x: any) => (
                <li key={x.id}><Link to={`/ajanlatok/${x.id}`}>{x.label}</Link> <span className={`badge ${QUOTE_STATUS_CLASS[x.status] ?? ''}`}>{STATUS_Q[x.status] ?? x.status}</span>
                  <span className="muted"> · bruttó {money(x.gross_total, x.currency)}{x.valid_until && ` · érvényes: ${fmtDay(x.valid_until)}`}</span></li>
              ))}
              {!(d as any).quotes?.length && <li className="muted">Még nincs ajánlat.</li>}
            </ul>
          </section>
          {invoices.length > 0 && (
            <section className="card">
              <h3>Számlák</h3>
              <ul className="plain">{invoices.map((i) => <li key={i.id}><Link to={`/szamlak/${i.id}`}>{i.number ?? '(kiállítás alatt)'}</Link> <span className="muted">· {i.kind_hu} · {money(i.gross_total, i.currency)} · {i.status_hu}</span></li>)}</ul>
            </section>
          )}
          <TaskList subject={{ type: 'deal', id: d.id }} reloadKey={tick} onChange={() => { reload(); setTick((x) => x + 1); }} />
        </div>
        <div>
          {s.on('ai.agents') && <AgentPanel subject={{ type: 'deal', id: d.id }} onChange={refresh} />}
          <Timeline path={`/deals/${d.id}`} reloadKey={tick} />
        </div>
      </div>
    </div>
  );
}
